import { Alert, Button, PhoneField } from '@design-system';
import { useTranslation } from 'next-i18next';
import { FC, useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Country, isValidPhoneNumber } from 'react-phone-number-input';

import { Image } from '../../../components/Image';
import { UserPreferencesContext } from '../../../context/UserPreferencesContext';
import { toE164 } from '../../../helpers/phone';
import { useAuth } from '../../../providers';

interface FormValues {
  phone: string;
}

interface Props {
  error: boolean;
  experimentalProductSmsCapture: boolean;
  imageUrl?: string;
  loading: boolean;
  onSubmit: (data: { phone: string }) => Promise<void>;
  onSkip: () => void;
  phone: string;
}

export const PhoneFormContainer: FC<Props> = ({
  error,
  experimentalProductSmsCapture,
  imageUrl,
  loading,
  onSkip,
  onSubmit,
  phone,
}) => {
  const { t } = useTranslation('authentication');
  const { preferences } = useContext(UserPreferencesContext);
  const { user } = useAuth();

  const defaultPhone = toE164(phone);

  const form = useForm<FormValues>({
    defaultValues: {
      phone: isValidPhoneNumber(defaultPhone) ? defaultPhone : '',
    },
    resolver: (values) => ({
      values,
      errors:
        !values.phone || !isValidPhoneNumber(values.phone)
          ? { phone: { type: 'validate', message: 'screens.phone.phoneError' } }
          : {},
    }),
  });

  const phoneError = form.formState.errors.phone?.message;

  return (
    <FormProvider {...form}>
      <form
        className="flex flex-col gap-y-6 px-6 pb-6"
        onSubmit={(ev) => void form.handleSubmit(onSubmit)(ev)}
      >
        {error && <Alert status="error">{t('errors.generic')}</Alert>}
        {experimentalProductSmsCapture ? (
          <div className="relative flex flex-col gap-y-3">
            {imageUrl && (
              <div className="relative z-10 mx-auto -mt-9 mb-3 h-24 w-24 -rotate-2">
                <Image
                  alt={t('screens.phone.title')}
                  className="overflow-hidden rounded-sm"
                  src={imageUrl}
                />
                <div className="pointer-events-none absolute -right-6 -top-6 cursor-default">
                  <Button
                    disabled
                    hideLabel
                    icon="ic_notification_active"
                    label=""
                    size="md"
                    variant="positive"
                  />
                </div>
              </div>
            )}
            <p className="w-full px-3 text-center font-bold md:text-md">
              {t('screens.phone.title')}
            </p>
            <p className="w-full text-balance text-center">
              {t('screens.phone.subtitle')}
            </p>
          </div>
        ) : (
          <>
            <p className="w-full text-balance text-center">
              {t('screens.phone.subtitle')}
            </p>
            <Image
              alt={t('screens.phone.title')}
              size={{ sm: { ratio: '2/1', span: 12 } }}
              shape="rounded"
              src={t('screens.phone.image')}
            />
          </>
        )}
        {isValidPhoneNumber(defaultPhone) ? null : (
          <PhoneField
            control={form.control}
            defaultCountry={
              (user?.countryCode || preferences.countryCode) as
                | Country
                | undefined
            }
            disabled={loading}
            error={phoneError && t(phoneError)}
            id="phone"
            name="phone"
          />
        )}
        <div className="flex flex-col gap-y-3">
          <Button
            disabled={loading}
            fullWidth
            label={t('screens.phone.button')}
            size="lg"
            type="submit"
            variant="primary"
          />
          <button
            className="underline hover:text-neutral-4"
            disabled={loading}
            onClick={() => void onSkip()}
            type="button"
          >
            {t('screens.phone.skip')}
          </button>
          {isValidPhoneNumber(defaultPhone) ? null : (
            <p className="text-center text-xs text-neutral-4">
              {t('screens.phone.phoneDisclaimer')}
            </p>
          )}
        </div>
      </form>
    </FormProvider>
  );
};
